import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { useVisualViewportHeight } from "@/hooks/useVisualViewportHeight"
import { cn } from "@/lib/utils"

// Walks the React child tree looking for any element whose component shares
// Radix's `DialogDescription` displayName. Both our `DialogDescription` and
// `SheetDescription` re-export the same Radix primitive, so a single check
// covers both wrappers. Used to decide whether to inject the sr-only fallback
// below.
function containsRadixDescription(node: React.ReactNode): boolean {
  let found = false
  const walk = (n: React.ReactNode): void => {
    if (found) return
    React.Children.forEach(n, (child) => {
      if (found) return
      if (!React.isValidElement(child)) return
      const type = child.type
      const displayName =
        typeof type === "string"
          ? null
          : (type as { displayName?: string }).displayName
      if (displayName === DialogPrimitive.Description.displayName) {
        found = true
        return
      }
      const childProps = child.props as
        | { children?: React.ReactNode }
        | null
        | undefined
      if (childProps?.children !== undefined) walk(childProps.children)
    })
  }
  walk(node)
  return found
}

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => {
  // Radix emits a console warning unless the dialog has a registered
  // `Description`. Rather than relying on every consumer to remember,
  // inject an sr-only fallback when no `DialogDescription` is present in
  // the subtree. Real consumer descriptions still take precedence — Radix
  // wires `aria-describedby` via context, and the fallback only renders
  // when the walk above finds nothing.
  const hasDescription = containsRadixDescription(children)
  // Keyboard-safe vertical positioning. The dialog is centred with
  // `top-[50%] translate-y-[-50%]`, but `top: 50%` resolves against the fixed
  // containing block = the LAYOUT viewport, which iOS Safari/iPadOS do NOT
  // shrink when the on-screen keyboard opens. A centred dialog therefore stays
  // pinned to the middle of the full-height layout viewport while the keyboard
  // covers the bottom half — burying the dialog's inputs and submit button with
  // no way to scroll to them (the repeatedly-reported "keyboard covers half the
  // screen, can't enter anything" bug). `window.visualViewport.height` is the
  // only value that tracks the keyboard, so we re-centre on the VISIBLE area
  // (`top: vvHeight/2`) and cap the dialog to it (`max-height: vvHeight - gutter`,
  // with internal scroll) whenever the API is present. Body scroll is locked by
  // Radix while open, so `visualViewport.offsetTop` stays ~0 and `top` aligned —
  // no scroll listener, no jitter (see useVisualViewportHeight). Falls back to
  // the CSS `top-[50%]` + `max-h-[calc(100dvh-2rem)]` where the API is absent
  // (SSR / older browsers). The hook is live only while the dialog is mounted,
  // i.e. open — Radix unmounts Content on close. NOTE: this assumes the default
  // unmount-on-close. `<DialogContent forceMount>` would keep the closed dialog
  // in the DOM with a live visualViewport `resize` listener re-rendering it on
  // every keyboard open/close — not used anywhere today and unsupported here; a
  // forceMount consumer must gate the hook on the open state itself.
  const vvHeight = useVisualViewportHeight()
  const vvStyle: React.CSSProperties | undefined =
    vvHeight != null
      ? { top: `${vvHeight / 2}px`, maxHeight: `calc(${vvHeight}px - 2rem)` }
      : undefined
  return (
    <DialogPortal>
      <DialogOverlay />
      {/* NOTE: `overflow-y-auto` makes this a scroll container, which CLIPS any
          `position: absolute`/`fixed` descendant (a transform here also traps
          `fixed`). Dropdowns/popovers/tooltips rendered inside a dialog MUST use
          `createPortal` (see UserSelect) — an in-flow absolute menu will be cut
          off. The scroll is load-bearing for the keyboard fix, so it stays. */}
      {/* NOTE: the `duration-200` utility below is mirrored by
          `RADIX_OPEN_ANIMATION_MS` in `./animation-tokens.ts`, which keyboard-safe
          deferred-focus logic keys off (see `useDeferredAutoFocus`). Change one →
          change the other. */}
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid max-h-[calc(100dvh-2rem)] w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto overscroll-contain border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          className
        )}
        {...props}
        // Keyboard-safe defaults; a consumer-supplied `style` still merges on top.
        style={{ ...vvStyle, ...props.style }}
      >
        {children}
        {!hasDescription && (
          <DialogPrimitive.Description className="sr-only">
            Dialog
          </DialogPrimitive.Description>
        )}
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
